import { Theme } from "@emotion/react";
import styled, { CSSObject } from "@emotion/styled";

import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import { BreakPointVals } from "@brexhq/shared/styles/breakpoints";
import {
  isDarkTheme,
  type ModuleThemeType,
} from "@brexhq/shared/types/ModuleTheme";

import { getMarginReverseByBreakpoint } from "../../shared/Grid";

/**
 * this function takes a breakpoint and moves the container over so that it is full width.
 * Then it adds padding to make the scroll bar move back into place in the grid, offset by buttons
 * @param theme theme to get spacing styling
 * @param breakpoint current breakpoint
 * @param extraCSS CSS that needs to be merged in with this styling
 * @returns CSSObject with all necessary margin/padding rules
 */
const getMarginOffsetStyleByBreakpoint = (
  theme: Theme,
  breakpoint: BreakPointVals,
  extraCSS?: CSSObject,
) => {
  const negativeGridMargin = getMarginReverseByBreakpoint(breakpoint);
  return {
    [theme.breakpoints[breakpoint]]: {
      marginRight: negativeGridMargin,
      marginLeft: negativeGridMargin,
      paddingLeft: `${theme.grid.wrapperOuterMargin[breakpoint]}px`,

      "::-webkit-scrollbar-track": {
        marginRight: `${theme.grid.wrapperOuterMargin[breakpoint]}px`,
        marginLeft: `${
          theme.grid.wrapperOuterMargin[breakpoint] + scrollButtonWidth
        }px`,
      },
      ...extraCSS,
    },
  };
};

/**
 * this function calculates how much to move the container over so that it is full width.
 * extra logic here is for the grid being a max width so spacing around it is variable.
 * Then it adds padding to make the scroll bar move back into place in the grid, offset by buttons
 *
 * @param theme theme to get spacing styling
 * @param offsetScrollLeft an offset for the left side of the scrollbar track
 * @param extraCSS CSS that needs to be merged in with this styling
 * @returns CSSObject with all necessary margin/padding rules
 */
const getMarginOffsetForXLRGBrowser = (
  theme: Theme,
  offsetScrollLeft?: number,
  extraCSS?: CSSObject,
) => {
  const negativeGridMargin = getMarginReverseByBreakpoint("pastGrid");
  return {
    [`@media(min-width: ${theme.maxWidths.moduleGrid})`]: {
      marginRight: negativeGridMargin,
      marginLeft: negativeGridMargin,
      paddingLeft: `calc(((100vw - ${theme.maxWidths.moduleGrid}) * .5 ) + ${theme.grid.wrapperOuterMargin.LRG}px)`,
      "::-webkit-scrollbar-track": {
        marginRight: `calc(((100vw - ${theme.maxWidths.moduleGrid}) * .5 ) + ${theme.grid.wrapperOuterMargin.LRG}px )`,
        marginLeft: `calc(((100vw - ${theme.maxWidths.moduleGrid}) * .5 ) + ${theme.grid.wrapperOuterMargin.LRG}px + ${offsetScrollLeft}px)`,
      },
      ...extraCSS,
    },
  };
};

const getBGColor = (moduleTheme: ModuleThemeType, theme: Theme) => {
  if (moduleTheme === "PrimaryGray") {
    return theme.colors.border.hover;
  }
  if (isDarkTheme(moduleTheme)) {
    return theme.colors.border.inverse.pressed;
  }
  return theme.colors.background.secondary;
};

/**
 * width of scroll buttons next to the scroll bar
 */
const scrollButtonWidth = 175;

/**
 * Scroll Controller is the container that holds all the scrollbar styling
 */
export const ScrollController = styled.div<{
  maxNumberOfScrolls: number;
  moduleTheme: ModuleThemeType;
  shouldSnap: boolean;
}>(({ theme, maxNumberOfScrolls, moduleTheme, shouldSnap }) => {
  const { standard } = getModuleThemeColors(moduleTheme);

  const scrollBarStyling: CSSObject = {
    "@supports not (selector(::-webkit-scrollbar-track) and selector(::-webkit-scrollbar-thumb) and selector(::-webkit-scrollbar-track-piece)) ":
      {
        scrollbarWidth: "none",
      },
    "::-webkit-scrollbar": {
      WebkitAppearance: "none",
      width: "0",
      height: "8px",
    },
    "::-webkit-scrollbar-track": {
      marginLeft: `${scrollButtonWidth}px`, //moves track over by size of arrow buttons
    },
    "::-webkit-scrollbar-thumb": {
      borderRadius: "0",
      background: standard.textPrimary,
    },
    "::-webkit-scrollbar-track-piece": {
      backgroundColor: getBGColor(moduleTheme, theme),
    },

    // For all sizes, I shift margin out left and right for grid,
    // then move the track bar back in to original position + shift over for buttons
    ...getMarginOffsetStyleByBreakpoint(theme, "LRG"),
    ...getMarginOffsetStyleByBreakpoint(theme, "MED"),
    ...getMarginOffsetStyleByBreakpoint(theme, "SML", {
      paddingBottom: theme.spacing.LRG.med,
      scrollbarWidth: "none",
      "::-webkit-scrollbar": {
        height: "0",
      },
    }),

    // when you get to browsers larger than module grid there is more variable padding so this is necessary.
    ...getMarginOffsetForXLRGBrowser(theme, scrollButtonWidth),
  };
  return {
    overflowY: "initial",
    overflowX: "auto",
    marginBottom: maxNumberOfScrolls === 0 ? theme.spacing.LRG.lrg : 0,
    scrollSnapType: shouldSnap ? "x mandatory" : "initial",
    scrollSnapStop: "normal",
    scrollBehavior: "smooth",
    paddingBottom: theme.spacing.LRG.xlrg,
    scrollbarColor: [standard.textPrimary, getBGColor(moduleTheme, theme)],
    scrollbarWidth: "thin",

    // negative margin here
    ...scrollBarStyling,
  };
});

export const InnerScroll = styled.div<{ paddingRight }>(({ paddingRight }) => {
  return {
    display: "inline-flex",
    alignItems: "stretch",
    paddingRight: `${paddingRight}px`,
  };
});

// Used to report exact width of the swipe card
export const CardMeasurer = styled.div({
  "*": {
    userSelect: "none",
  },
});

export const NonWebkitProgressContainer = styled.div(({ theme }) => {
  return {
    display: "none",
    "@supports not (selector(::-webkit-scrollbar-track) and selector(::-webkit-scrollbar-thumb) and selector(::-webkit-scrollbar-track-piece)) ":
      {
        display: "inline-flex",
        width: "100%",
        alignItems: "center",
        transform: "translateY(-30px)", //30 px moves the buttons in line with the scroll bar
        paddingLeft: theme.spacing.LRG.med,
      },
    [theme.breakpoints.MED]: { display: "none" },
  };
});

export const ScrollButtonContainer = styled.div(({ theme }) => {
  return {
    // to account for arrows
    transform: "translateY(-30px)", //30 px moves the buttons in line with the scroll bar
    display: "inline-block",
    zIndex: 5,
    [theme.breakpoints.SML]: {
      transform: "none",
      width: "100%",
    },
  };
});
